import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getProfileById, getUserProfile } from '../lib/services/ProfileService';

export interface DisplayProfile {
  /** Prénom pour le salut (« Bonjour Adam ») */
  firstName: string;
  /** Nom affiché court dans la barre (prénom, sinon nom, sinon email) */
  displayName: string;
  /** URL photo profil BD, sinon Google */
  avatarUrl: string | null;
  loading: boolean;
}

function capitalizeFirst(s: string): string {
  const t = s.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function emailLocalName(email?: string | null): string {
  const local = (email || '').split('@')[0] || 'Utilisateur';
  return capitalizeFirst(local.replace(/[._-]+/g, ' '));
}

/**
 * Prénom / avatar pour Header & Dashboard.
 * En impersonation : profil de l'utilisateur cible (pas l'admin).
 */
export function useDisplayProfile(): DisplayProfile {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId, impersonatedEmail } = useImpersonation();
  const [firstName, setFirstName] = useState(() =>
    emailLocalName(isImpersonating ? impersonatedEmail : user?.email),
  );
  const [displayName, setDisplayName] = useState(() =>
    emailLocalName(isImpersonating ? impersonatedEmail : user?.email),
  );
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user) {
        setFirstName('Utilisateur');
        setDisplayName('Utilisateur');
        setAvatarUrl(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        if (isImpersonating && impersonatedUserId) {
          const { data: profile } = await getProfileById(
            impersonatedUserId,
            impersonatedEmail || '',
          );
          if (cancelled) return;

          const prenom = (profile?.prenom || '').trim();
          const nom = (profile?.nom || '').trim();
          const email = profile?.email || impersonatedEmail || '';

          const resolvedFirst = prenom
            ? capitalizeFirst(prenom)
            : emailLocalName(email);
          const resolvedDisplay = prenom
            ? capitalizeFirst(prenom)
            : nom
              ? capitalizeFirst(nom)
              : emailLocalName(email);

          setFirstName(resolvedFirst);
          setDisplayName(resolvedDisplay);
          setAvatarUrl((profile?.avatar_url && profile.avatar_url.trim()) || null);
          return;
        }

        const meta = user.user_metadata || {};
        const googlePicture =
          (typeof meta.avatar_url === 'string' && meta.avatar_url) ||
          (typeof meta.picture === 'string' && meta.picture) ||
          null;
        const googleGiven =
          (typeof meta.given_name === 'string' && meta.given_name.trim()) ||
          (typeof meta.prenom === 'string' && meta.prenom.trim()) ||
          '';

        const { data: profile } = await getUserProfile();
        if (cancelled) return;

        const prenom = (profile?.prenom || '').trim() || googleGiven;
        const nom = (profile?.nom || '').trim();

        const resolvedFirst = prenom
          ? capitalizeFirst(prenom)
          : emailLocalName(user.email);

        const resolvedDisplay = prenom
          ? capitalizeFirst(prenom)
          : nom
            ? capitalizeFirst(nom)
            : emailLocalName(user.email);

        const resolvedAvatar =
          (profile?.avatar_url && profile.avatar_url.trim()) || googlePicture || null;

        setFirstName(resolvedFirst);
        setDisplayName(resolvedDisplay);
        setAvatarUrl(resolvedAvatar);
      } catch (e) {
        console.warn('[useDisplayProfile]', e);
        if (!cancelled) {
          const fallbackEmail = isImpersonating ? impersonatedEmail : user.email;
          setFirstName(emailLocalName(fallbackEmail));
          setDisplayName(emailLocalName(fallbackEmail));
          if (!isImpersonating) {
            const meta = user.user_metadata || {};
            setAvatarUrl(
              (typeof meta.avatar_url === 'string' && meta.avatar_url) ||
                (typeof meta.picture === 'string' && meta.picture) ||
                null,
            );
          } else {
            setAvatarUrl(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.email, isImpersonating, impersonatedUserId, impersonatedEmail]);

  return { firstName, displayName, avatarUrl, loading };
}
